import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Row, Col, Card, CardBody, Progress, Modal, ModalBody, Spinner } from "reactstrap";
import Chart from "react-apexcharts";
import { getPlatformOverviewReport, getLoggedInUser } from "../../../helpers/fakebackend_helper";
import "./platformOverview.scss";

// Validated categorical / status colors (see dataviz skill reference palette.md)
const palette = {
  blue: "#2a78d6",
  orange: "#eb6834",
  aqua: "#1baf7a",
  violet: "#4a3aa7",
  good: "#0ca30c",
  warning: "#fab219",
  critical: "#d03b3b",
  muted: "#898781",
  grid: "#e1e0d9",
};

const kpiMeta = [
  { valueKey: "totalRegistered", trendKey: "totalRegisteredTrend", label: "Total Registered", icon: "mdi mdi-account-group-outline", bg: palette.blue },
  { valueKey: "approved", trendKey: "approvedTrend", label: "Approved", icon: "mdi mdi-check-circle-outline", bg: palette.good },
  { valueKey: "pendingApprovalCount", trendKey: "pendingApprovalCountTrend", label: "Pending Approval", icon: "mdi mdi-timer-sand", bg: palette.warning },
  { valueKey: "activeSubscriptions", trendKey: "activeSubscriptionsTrend", label: "Active Subscriptions", icon: "mdi mdi-crown-outline", bg: palette.aqua },
  { valueKey: "expiredSubscriptions", trendKey: "expiredSubscriptionsTrend", label: "Expired Subscriptions", icon: "mdi mdi-calendar-remove-outline", bg: palette.critical },
  { valueKey: "expiringIn7Days", trendKey: "expiringIn7DaysTrend", label: "Expiring in 7 Days", icon: "mdi mdi-clock-alert-outline", bg: palette.violet },
];

const renewalMeta = {
  "Due Today": { color: palette.critical, icon: "mdi mdi-alert-circle-outline" },
  "Due Tomorrow": { color: palette.warning, icon: "mdi mdi-clock-outline" },
  "Due in Next 7 Days": { color: palette.blue, icon: "mdi mdi-calendar-week-outline" },
  "Due in Next 30 Days": { color: palette.aqua, icon: "mdi mdi-calendar-month-outline" },
  "Renewed Today": { color: palette.good, icon: "mdi mdi-check-circle-outline" },
};
const defaultRenewalMeta = { color: palette.muted, icon: "mdi mdi-calendar-outline" };

const activityMeta = {
  "Logins": { icon: "mdi mdi-account-check-outline", bg: palette.good },
  "Mock Tests": { icon: "mdi mdi-file-document-outline", bg: palette.blue },
  "Questions Practiced": { icon: "mdi mdi-help-circle-outline", bg: palette.warning },
  "Bookmarks": { icon: "mdi mdi-bookmark-outline", bg: palette.violet },
};
const defaultActivityMeta = { icon: "mdi mdi-chart-box-outline", bg: palette.muted };

const planColorCycle = [palette.good, palette.blue, palette.warning, palette.violet, palette.aqua, palette.orange];

const calendarColor = (count) => {
  if (!count) return palette.muted;
  if (count <= 5) return palette.good;
  if (count <= 15) return palette.warning;
  return palette.critical;
};

const formatINR = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const CardHeader = ({ icon, title, color, viewAll, onViewAll }) => (
  <div className="pov-card-header" style={{ backgroundColor: `${color}1a` }}>
    <div className="pov-card-header-left">
      <div className="pov-card-header-icon" style={{ backgroundColor: color }}>
        <i className={icon}></i>
      </div>
      <div className="pov-card-header-title">{title}</div>
    </div>
    {viewAll && (
      <span className="pov-card-link" style={{ cursor: "pointer" }} onClick={onViewAll}>View All</span>
    )}
  </div>
);

const MiniTableHead = ({ color, columns }) => (
  <thead style={{ backgroundColor: `${color}22` }}>
    <tr>
      {columns.map((col, idx) => {
        const label = typeof col === "string" ? col : col.label;
        const width = typeof col === "string" ? undefined : col.width;
        return <th key={idx} style={width ? { width } : undefined}>{label}</th>;
      })}
    </tr>
  </thead>
);

const Cell = ({ value }) => (
  <span className="pov-cell-truncate" title={value}>{value}</span>
);

const EmptyRow = ({ span }) => (
  <tr><td colSpan={span} className="text-center text-muted py-3">No records</td></tr>
);

const ExpiringSoonTable = ({ rows }) => (
  <table className="pov-mini-table">
    <MiniTableHead
      color={palette.warning}
      columns={[
        { label: "Student", width: "26%" },
        { label: "Mobile", width: "22%" },
        { label: "Plan", width: "18%" },
        { label: "Expiry", width: "18%" },
        { label: "Days", width: "16%" },
      ]}
    />
    <tbody>
      {rows.length === 0 && <EmptyRow span={5} />}
      {rows.map((row, idx) => (
        <tr key={idx}>
          <td><Cell value={row.name} /></td>
          <td><Cell value={row.mobile} /></td>
          <td><Cell value={row.plan} /></td>
          <td><Cell value={row.expiry} /></td>
          <td>
            <span className="pov-pill-badge" style={{ backgroundColor: row.days <= 3 ? palette.critical : palette.warning }}>
              {row.days}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ExpiredSubsTable = ({ rows }) => (
  <table className="pov-mini-table">
    <MiniTableHead
      color={palette.critical}
      columns={[
        { label: "Student", width: "32%" },
        { label: "Mobile", width: "26%" },
        { label: "Expired On", width: "24%" },
        { label: "Days", width: "18%" },
      ]}
    />
    <tbody>
      {rows.length === 0 && <EmptyRow span={4} />}
      {rows.map((row, idx) => (
        <tr key={idx}>
          <td><Cell value={row.name} /></td>
          <td><Cell value={row.mobile} /></td>
          <td><Cell value={row.expiredOn} /></td>
          <td>
            <span className="pov-pill-badge" style={{ backgroundColor: palette.muted }}>{row.days}</span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PendingApprovalTable = ({ rows }) => (
  <table className="pov-mini-table">
    <MiniTableHead
      color={palette.violet}
      columns={[
        { label: "Student", width: "36%" },
        { label: "Mobile", width: "30%" },
        { label: "Registered", width: "34%" },
      ]}
    />
    <tbody>
      {rows.length === 0 && <EmptyRow span={3} />}
      {rows.map((row, idx) => (
        <tr key={idx}>
          <td><Cell value={row.name} /></td>
          <td><Cell value={row.mobile} /></td>
          <td><Cell value={row.registered} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RecentRegistrationsTable = ({ rows }) => (
  <table className="pov-mini-table">
    <MiniTableHead
      color={palette.blue}
      columns={[
        { label: "Student", width: "36%" },
        { label: "Mobile", width: "30%" },
        { label: "Registered", width: "34%" },
      ]}
    />
    <tbody>
      {rows.length === 0 && <EmptyRow span={3} />}
      {rows.map((row, idx) => (
        <tr key={idx}>
          <td><Cell value={row.name} /></td>
          <td><Cell value={row.mobile} /></td>
          <td><Cell value={row.time} /></td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RenewalsList = ({ rows }) => (
  <div>
    {rows.map((item, idx) => {
      const meta = renewalMeta[item.label] || defaultRenewalMeta;
      return (
        <div className="pov-renewal-row" key={idx} style={{ backgroundColor: `${meta.color}14` }}>
          <div className="pov-renewal-row-left">
            <span className="pov-renewal-icon" style={{ backgroundColor: meta.color }}>
              <i className={meta.icon}></i>
            </span>
            <span>{item.label}</span>
          </div>
          <span className="pov-pill-badge" style={{ backgroundColor: meta.color }}>{item.value}</span>
        </div>
      );
    })}
  </div>
);

const CalendarTable = ({ rows }) => (
  <table className="pov-mini-table">
    <MiniTableHead color={palette.good} columns={["Date", "Subscriptions Expiring"]} />
    <tbody>
      {rows.map((row, idx) => (
        <tr key={idx}>
          <td>{row.date}</td>
          <td>
            <span className="pov-pill-badge" style={{ backgroundColor: calendarColor(row.count) }}>{row.count}</span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const modalMeta = {
  renewals: { title: "Subscription Renewals", icon: "mdi mdi-autorenew", gradient: "linear-gradient(135deg, #1baf7a, #0c6e4a)" },
  expiringSoon: { title: "Expiring Soon (Next 7 Days)", icon: "mdi mdi-calendar-alert-outline", gradient: "linear-gradient(135deg, #fab219, #b97600)" },
  expiredSubs: { title: "Expired Subscriptions", icon: "mdi mdi-calendar-remove-outline", gradient: "linear-gradient(135deg, #d03b3b, #832323)" },
  pendingApproval: { title: "Pending Approval", icon: "mdi mdi-clock-alert-outline", gradient: "linear-gradient(135deg, #4a3aa7, #2a2064)" },
  recentRegistrations: { title: "Recent Registrations", icon: "mdi mdi-account-plus-outline", gradient: "linear-gradient(135deg, #2a78d6, #144173)" },
  calendar: { title: "Upcoming Expiry Calendar", icon: "mdi mdi-calendar-clock-outline", gradient: "linear-gradient(135deg, #0ca30c, #075c07)" },
};

const PlatformOverviewReport = () => {
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoadError(false);
    try {
      const user = getLoggedInUser();
      const response = await getPlatformOverviewReport({ user_id: Number(user.uid) });
      if (!response || response.statusFlag === 2 || response.status === "401" || response.status === 401) {
        setLoadError(true);
        setReportData(null);
      } else {
        setReportData(response);
      }
    } catch (err) {
      setLoadError(true);
      setReportData(null);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Reports | Platform Overview";
    const headerEl = document.getElementById("headerTitle");
    if (headerEl) headerEl.innerHTML = "Dashboard";
    fetchReport();
  }, [fetchReport]);

  const closeModal = () => setActiveModal(null);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchReport();
  };

  const expiringSoon = reportData?.expiringSoon || [];
  const expiredSubs = reportData?.expiredSubs || [];
  const pendingApproval = reportData?.pendingApproval || [];
  const recentRegistrations = reportData?.recentRegistrations || [];
  const renewals = reportData?.renewals || [];
  const calendarDays = reportData?.calendarDays || [];
  const subscriptionPlans = reportData?.subscriptionPlans || [];
  const studentActivity = reportData?.studentActivity || [];
  const studentEngagement = reportData?.studentEngagement || [];

  const registrationsOptions = {
    chart: { type: "area", toolbar: { show: false }, fontFamily: "inherit" },
    dataLabels: { enabled: true, offsetY: -6, style: { fontSize: "10px", colors: [palette.muted] } },
    stroke: { curve: "smooth", width: 2, colors: [palette.blue] },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 } },
    colors: [palette.blue],
    markers: { size: 3, colors: [palette.blue], strokeWidth: 0 },
    xaxis: { categories: reportData?.registrationsCategories || [], labels: { style: { colors: palette.muted } } },
    yaxis: { labels: { style: { colors: palette.muted } } },
    grid: { borderColor: palette.grid },
    tooltip: { y: { formatter: (val) => `${val} registrations` } },
  };
  const registrationsSeries = [{ name: "Registrations", data: reportData?.registrationsValues || [] }];

  const subscriptionStatusOptions = {
    chart: { type: "donut", fontFamily: "inherit" },
    labels: ["Active", "Expiring Soon", "Expired"],
    colors: [palette.good, palette.warning, palette.critical],
    legend: { show: false },
    dataLabels: { enabled: true, style: { fontSize: "11px" } },
    stroke: { width: 2, colors: ["#fff"] },
    plotOptions: { pie: { donut: { size: "65%" } } },
  };
  const subscriptionStatusSeries = reportData?.subscriptionStatusSeries || [0, 0, 0];

  const modalRows = useMemo(() => ({
    renewals,
    expiringSoon,
    expiredSubs,
    pendingApproval,
    recentRegistrations,
    calendar: calendarDays,
  }), [renewals, expiringSoon, expiredSubs, pendingApproval, recentRegistrations, calendarDays]);

  if (isLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5">
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5">
          <i className="mdi mdi-cloud-alert-outline" style={{ fontSize: "48px", color: palette.critical }}></i>
          <p className="mt-2 mb-3">Couldn't load the dashboard. Please try again.</p>
          <button type="button" className="pov-refresh-btn" onClick={fetchReport}>
            <i className="mdi mdi-refresh"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="pov-toolbar">
          <button type="button" className="pov-refresh-btn" onClick={handleRefresh} disabled={refreshing}>
            <i className={`mdi mdi-refresh ${refreshing ? "pov-spin" : ""}`}></i> Refresh
          </button>
        </div>

        <Row className="g-2">
          {kpiMeta.map((meta, idx) => {
            const trend = reportData?.[meta.trendKey];
            return (
              <Col lg={2} md={4} sm={6} xs={12} key={idx}>
                <Card className="pov-kpi-card">
                  <CardBody className="d-flex align-items-center gap-3">
                    <div className="pov-kpi-icon" style={{ backgroundColor: meta.bg }}>
                      <i className={meta.icon}></i>
                    </div>
                    <div>
                      <div className="pov-kpi-value">{Number(reportData?.[meta.valueKey] || 0).toLocaleString()}</div>
                      <div className="pov-kpi-label">{meta.label}</div>
                      {trend && (
                        <div className={`pov-kpi-trend ${trend.up ? "pov-trend-up" : "pov-trend-down"}`}>
                          <i className={`mdi mdi-arrow-${trend.up ? "up" : "down"}`}></i> {trend.text}
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>

        <Row className="g-2 mt-1">
          <Col xl={5} lg={6}>
            <Card className="pov-card">
              <CardBody>
                <div className="card-title mb-2">Student Registrations (Last 12 Months)</div>
                <div className="pov-chart-legend mb-1">
                  <span><span className="pov-legend-dot" style={{ backgroundColor: palette.blue }}></span>Registrations</span>
                </div>
                <Chart options={registrationsOptions} series={registrationsSeries} type="area" height={260} />
              </CardBody>
            </Card>
          </Col>
          <Col xl={2} lg={6}>
            <Card className="pov-card">
              <CardBody>
                <div className="card-title mb-2">Subscription Status</div>
                <Chart options={subscriptionStatusOptions} series={subscriptionStatusSeries} type="donut" height={210} />
                <div className="pov-chart-legend">
                  <span><span className="pov-legend-dot" style={{ backgroundColor: palette.good }}></span>Active ({subscriptionStatusSeries[0] || 0})</span>
                  <span><span className="pov-legend-dot" style={{ backgroundColor: palette.warning }}></span>Expiring ({subscriptionStatusSeries[1] || 0})</span>
                  <span><span className="pov-legend-dot" style={{ backgroundColor: palette.critical }}></span>Expired ({subscriptionStatusSeries[2] || 0})</span>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={2} lg={6}>
            <Card className="pov-revenue-card">
              <CardBody>
                <div className="card-title mb-2" style={{ color: "#fff" }}>Revenue Overview</div>
                <div className="pov-revenue-label">This Month</div>
                <div className="pov-revenue-value">{formatINR(reportData?.revenueThisMonth)}</div>
                {reportData?.revenueThisMonthTrend && (
                  <div className={`pov-kpi-trend ${reportData.revenueThisMonthTrend.up ? "pov-trend-up" : "pov-trend-down"}`} style={{ color: reportData.revenueThisMonthTrend.up ? "#b9f6ca" : "#ffb3b3" }}>
                    <i className={`mdi mdi-arrow-${reportData.revenueThisMonthTrend.up ? "up" : "down"}`}></i> {reportData.revenueThisMonthTrend.text}
                  </div>
                )}
                <div className="pov-revenue-divider"></div>
                <div className="pov-revenue-label">This Year</div>
                <div className="pov-revenue-value">{formatINR(reportData?.revenueThisYear)}</div>
                <div className="pov-revenue-divider"></div>
                <div className="pov-revenue-label">Total Revenue</div>
                <div className="pov-revenue-value">{formatINR(reportData?.revenueTotal)}</div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} lg={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-autorenew" title="Subscription Renewals" color={palette.aqua} viewAll onViewAll={() => setActiveModal("renewals")} />
                <RenewalsList rows={renewals} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-2 mt-1">
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-calendar-alert-outline" title="Expiring Soon (Next 7 Days)" color={palette.warning} viewAll onViewAll={() => setActiveModal("expiringSoon")} />
                <ExpiringSoonTable rows={expiringSoon.slice(0, 5)} />
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-calendar-remove-outline" title="Expired Subscriptions" color={palette.critical} viewAll onViewAll={() => setActiveModal("expiredSubs")} />
                <ExpiredSubsTable rows={expiredSubs.slice(0, 5)} />
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-clock-alert-outline" title="Pending Approval" color={palette.violet} viewAll onViewAll={() => setActiveModal("pendingApproval")} />
                <PendingApprovalTable rows={pendingApproval.slice(0, 5)} />
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-account-plus-outline" title="Recent Registrations" color={palette.blue} viewAll onViewAll={() => setActiveModal("recentRegistrations")} />
                <RecentRegistrationsTable rows={recentRegistrations.slice(0, 5)} />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-2 mt-1">
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-chart-donut" title="Subscription Plans" color={palette.blue} />
                {subscriptionPlans.length === 0 && <p className="text-muted text-center py-3 mb-0">No plans yet</p>}
                {subscriptionPlans.map((plan, idx) => (
                  <div className="mb-2" key={idx}>
                    <div className="d-flex justify-content-between" style={{ fontSize: "12.5px" }}>
                      <span>{plan.label}</span>
                      <span className="text-muted">{plan.value} ({plan.pct}%)</span>
                    </div>
                    <Progress value={plan.pct} style={{ height: 6, backgroundColor: "#f1f1f5" }} barStyle={{ backgroundColor: planColorCycle[idx % planColorCycle.length] }} />
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-lightning-bolt-outline" title="Student Activity (Today)" color={palette.warning} />
                <Row className="g-2">
                  {studentActivity.map((item, idx) => {
                    const meta = activityMeta[item.label] || defaultActivityMeta;
                    return (
                      <Col xs={6} key={idx}>
                        <div className="pov-activity-tile">
                          <div className="pov-activity-icon" style={{ backgroundColor: meta.bg }}>
                            <i className={meta.icon}></i>
                          </div>
                          <div>
                            <div className="pov-activity-value">{Number(item.value || 0).toLocaleString()}</div>
                            <div className="pov-activity-label">{item.label}</div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-calendar-clock-outline" title="Upcoming Expiry Calendar" color={palette.good} viewAll onViewAll={() => setActiveModal("calendar")} />
                <div className="pov-calendar-strip">
                  {calendarDays.map((day, idx) => (
                    <div className="pov-calendar-day" key={idx}>{day.date}</div>
                  ))}
                </div>
                <div className="pov-calendar-counts">
                  {calendarDays.map((day, idx) => (
                    <div className="pov-calendar-count" key={idx}>
                      <div className="pov-calendar-circle" style={{ backgroundColor: calendarColor(day.count) }}>{day.count}</div>
                    </div>
                  ))}
                </div>
                <div className="text-muted text-center mt-2" style={{ fontSize: "11px" }}>Subscriptions Expiring</div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={3} md={6}>
            <Card className="pov-card">
              <CardBody>
                <CardHeader icon="mdi mdi-heart-outline" title="Student Engagement" color={palette.violet} />
                {studentEngagement.map((item, idx) => (
                  <div className="pov-side-list-item" key={idx}>
                    <span>{item.label}</span>
                    <strong>{Number(item.value || 0).toLocaleString()}</strong>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <div className="pov-footer-tip">
          <div><i className="mdi mdi-star text-warning me-1"></i> <strong>Pro Tip:</strong> Encourage students to practice daily and take mock tests regularly for better preparation and results.</div>
          <div><i className="mdi mdi-clock-outline me-1"></i>All times in IST{reportData?.lastUpdated ? ` • Updated ${reportData.lastUpdated}` : ""}</div>
        </div>
      </div>

      <Modal isOpen={!!activeModal} toggle={closeModal} size="lg" scrollable centered contentClassName="pov-modal-content">
        {activeModal && (
          <>
            <div className="pov-modal-header" style={{ background: modalMeta[activeModal].gradient }}>
              <div className="pov-modal-header-left">
                <div className="pov-modal-header-icon">
                  <i className={modalMeta[activeModal].icon}></i>
                </div>
                <div>
                  <div className="pov-modal-header-title">{modalMeta[activeModal].title}</div>
                  <div className="pov-modal-header-subtitle">{modalRows[activeModal].length} total records</div>
                </div>
              </div>
              <button type="button" className="pov-modal-close-btn" onClick={closeModal} aria-label="Close">
                <i className="mdi mdi-close"></i>
              </button>
            </div>
            <ModalBody className="pov-modal-body">
              {activeModal === "renewals" && <RenewalsList rows={renewals} />}
              {activeModal === "expiringSoon" && <ExpiringSoonTable rows={expiringSoon} />}
              {activeModal === "expiredSubs" && <ExpiredSubsTable rows={expiredSubs} />}
              {activeModal === "pendingApproval" && <PendingApprovalTable rows={pendingApproval} />}
              {activeModal === "recentRegistrations" && <RecentRegistrationsTable rows={recentRegistrations} />}
              {activeModal === "calendar" && <CalendarTable rows={calendarDays} />}
            </ModalBody>
          </>
        )}
      </Modal>
    </div>
  );
};

export default PlatformOverviewReport;
